import { useRef, useState } from 'react';

import { AutocompleteInputChangeReason } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSWRInfinite from 'swr/infinite';

import { AsyncAutocomplete, AsyncAutocompleteProps } from '~components';
import { IdReference, OffsetPagedResults } from '~interfaces';
import i18n from '~lib/i18n';

import SkcDeviceGroup from '../../interfaces/skcDeviceGroup';
import SkcUserGroup from '../../interfaces/skcUserGroup';
import SkcUserGroupsService from '../../services/skcUserGroupsService';

const service = new SkcUserGroupsService();

interface SkcUserGroupAutocompleteProps
	extends Omit<AsyncAutocompleteProps<SkcUserGroup>, 'options' | 'label'> {
	label?: string;
	count?: number;
	organisation?: IdReference;
}

const SkcUserGroupsAutocomplete = ({
	label = i18n.t('accessGroup'),
	count = 10,
	organisation,
	...asyncAutocompleteProps
}: SkcUserGroupAutocompleteProps) => {
	const { t } = useTranslation();

	const [searchQuery, setSearchQuery] = useState('');
	const [open, setOpen] = useState(false);

	const total = useRef<number>(undefined);
	const page = useRef(1);

	const getKey = (i: number, previousData: OffsetPagedResults<SkcDeviceGroup[]>) => {
		if (!open || (previousData && !previousData.hasMore)) {
			// reached the end
			return null;
		}

		page.current = i + 1;

		return [
			service.basePath,
			{
				page: page.current,
				pageSize: count,
				organisationId: organisation?.id,
				searchQuery: searchQuery,
			},
		] as const;
	};

	const { data, isLoading, isValidating, size, setSize } = useSWRInfinite(
		getKey,
		([_, args]) => service.getUserGroups(args),
		{
			keepPreviousData: true,
			revalidateFirstPage: false,
			onSuccess: (res) => (total.current = res[0].total),
		}
	);

	/**
	 * Just set the size of the pages
	 */
	const handleOverflow = () => {
		if (total.current != null && (size - 1) * count <= total.current) {
			setSize(size + 1);
		}
	};

	const handleInputChange = (
		e: React.SyntheticEvent<Element, Event>,
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		setSearchQuery(value);

		asyncAutocompleteProps.onInputChange?.(e, value, reason);
	};

	return (
		<AsyncAutocomplete
			{...asyncAutocompleteProps}
			label={label}
			loading={isLoading || isValidating}
			noOptionsText={t('noResults')}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			options={data?.flatMap((el) => el.results) ?? []}
			onInputChange={handleInputChange}
			onOverflow={handleOverflow}
		/>
	);
};

export default SkcUserGroupsAutocomplete;
